import React, { useContext, useState } from "react";
import { Copy, ExternalLink } from "lucide-react";
import { getSignedDownloadUrl } from "../../../../services/files";
import { ErrorModalContext } from "../../../../components/AlertsModal/AlertsModal";
import { PaymentDetailFieldProps } from "../interfaces";
import { formatPaymentFieldValue } from "../utils";
import { FieldInfo } from "./FieldInfo";

export const PaymentDetailField: React.FC<PaymentDetailFieldProps> = ({
  info,
  isCopyable,
  label,
  type = "text",
  value,
}) => {
  const { showAlert } = useContext(ErrorModalContext);
  const [isOpeningFile, setIsOpeningFile] = useState<boolean>(false);

  /**
   * YOSHI-10935: receipts live in a private bucket, so they open through a signed URL. A URL the
   * backend cannot sign (e.g. one the external system sent) is opened as is.
   */
  const openFile = async (): Promise<void> => {
    const fileUrl: string = value.toString();
    try {
      setIsOpeningFile(true);
      const signedUrl: string = await getSignedDownloadUrl(fileUrl);
      window.open(signedUrl, "_blank");
    } catch (error) {
      console.error("Error getting signed URL:", error);
      window.open(fileUrl, "_blank");
    } finally {
      setIsOpeningFile(false);
    }
  };

  // YOSHI-10444: shared with the PDF export so a value never reads differently on paper.
  const formatValue = (): string => formatPaymentFieldValue(value, type);

  const copyValue = (): void => {
    navigator.clipboard.writeText(value.toString());
    showAlert(
      `${label}: copiado correctamente`,
      "info",
      "Copiado al portapapeles",
    );
  };

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <p className="text-sm text-gray-500">{`${label}:`}</p>
          {info && <FieldInfo info={info} />}
        </div>
        {type === "file" ? (
          <button
            type="button"
            className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline disabled:text-gray-400"
            onClick={openFile}
            disabled={isOpeningFile}
          >
            {isOpeningFile ? "Abriendo archivo..." : "Ver comprobante"}
            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          </button>
        ) : (
          <p
            className={`break-words text-sm font-medium text-gray-700 ${
              type === "cardNumber" && "tracking-wide"
            }`}
          >
            {formatValue()}
          </p>
        )}
      </div>
      {isCopyable && value && (
        <button
          className="h-fit rounded-md bg-white p-1.5 text-gray-500 transition-colors duration-300 hover:bg-gray-100 hover:text-gray-900"
          onClick={copyValue}
        >
          <Copy className="h-4 w-4 shrink-0" />
        </button>
      )}
    </div>
  );
};
